import {
  ACCESS_TOKEN_PREFER_HEADER,
  BRANDING,
  FORM_URL_ENCODED
} from '../../constants/common-enums';
import fetchReq, { defaultHeaders } from '../../helpers/fetch';
import isEmptyString from '../../helpers/is-empty-string';
import StaticIdSingletonClass from '../../helpers/static-id';
import CONFIG from '../../settings';
import { getErmEnabledCookieValues } from '../ssi-erm/ssi-erm-cookie-manager';

const API = {
  /**
   * Generate Access Token api
   *  - returns access_token with scope when response is 200 ok.
   *  - TODO Unit test script
   * @param {string} authCode
   * @param {string} sessionId
   * @param {object} data
   * @returns {object} response object
   */
  generateAccessToken(authCode, sessionId, data = {}) {
    const base64ApiKey = btoa(StaticIdSingletonClass.apikey);
    let reqContext;
    sessionId = sessionId || CONFIG.application.defaults.dfpSessionId;
    const commonContext = {
      SubContext: 'CONSUMER_LIGHTBOX',
      channel: 'Web',
      dfpSessionId: sessionId,
      eventSource: 'LightboxTW'
    };
    const loginContext = {
      context: 'LOGIN',
      cookieId: '',
      eventSubType: data.eventSubType || 'AUTH_CODE'
    };
    if (data.context === 'ENROLLMENT') {
      reqContext = { ...commonContext, ...data };
      delete reqContext.pId;
    } else {
      reqContext = { ...commonContext, ...loginContext };
    }
    const requestPayload = {
      code: authCode,
      context: JSON.stringify(reqContext),
      grant_type: 'authorization_code'
    };

    /* Generate Access Token service updated with new ARM End point **/
    const options = {
      body: requestPayload,
      headers: {
        Authorization: `Basic ${base64ApiKey}`,
        'Content-Type': FORM_URL_ENCODED
      },
      method: 'POST',
      url: CONFIG.application.api.token.auth(StaticIdSingletonClass.namespace)
    };
    return fetchReq(options);
  },

  /**
   * Generate AccessToken from Id Token
   *  - returns access_token with scope when response is 200 ok.
   * @param {string} idToken
   * @param {string} apikey
   * @param {string} sessionId
   * @returns {object} response object
   */
  generateAccessTokenFromIdToken(
    idToken,
    apikey = StaticIdSingletonClass.apikey,
    sessionId = CONFIG.application.defaults.dfpSessionId
  ) {
    const requestPayload = {
      assertion: idToken,
      function: 'LOGIN@FED',
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      'software_statement_claimset.oauth2_version': '1.0',
      'software_statement_claimset.software_id': BRANDING.SRC,
      'software_statement_claimset.software_version': '22.04.00',
      'software_statement_claimset.tenancy_context.product_code': 'VXO',
      'software_statement_claimset.uebas[0].ueba_id': sessionId,
      'software_statement_claimset.uebas[0].ueba_source': 'THREAT_METRIX'
    };
    const headers = {
      Accept: 'application/json',
      Authorization: `Basic ${window.btoa(apikey)}`,
      'Content-Type': FORM_URL_ENCODED,
      Prefer: ACCESS_TOKEN_PREFER_HEADER
    };

    const options = {
      body: requestPayload,
      headers,
      method: 'POST',
      url: CONFIG.application.api.token.auth(StaticIdSingletonClass.namespace)
    };
    return fetchReq(options);
  },

  generateXAuthCode(accessToken) {
    return fetchReq({
      body: {
        grant_type: 'token_spawn',
        response_mode: ['urn:ext:oauth:response-mode:body'],
        response_type: ['code'],
        subject_token: accessToken
      },
      method: 'POST',
      url: CONFIG.application.api.token.spawn(StaticIdSingletonClass.namespace)
    });
  },

  renewSession(accessToken, sessionId) {
    const headers = {
      ...defaultHeaders,
      Authorization: `Bearer ${accessToken}`,
      'X-THMID': sessionId
    };

    const options = {
      body: {
        grant_type: 'access_token_renewal'
      },
      headers,
      method: 'POST',
      url: CONFIG.application.api.token.renewal(StaticIdSingletonClass.namespace)
    };

    return fetchReq(options);
  },

  revokeToken(accessToken, type, idToken) {
    const url = CONFIG.application.api.token.revoke(StaticIdSingletonClass.namespace);
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded'
    };
    const requestPayload = {
      token: idToken || accessToken,
      token_type_hint: type
    };
    const options = {
      body: requestPayload,
      headers,
      method: 'POST',
      url
    };
    return fetchReq(options);
  },

  revokeTokens(accessToken, types, idTokens, ignoreAccessToken = false) {
    const url = `${CONFIG.application.api.token.revoke(
      StaticIdSingletonClass.namespace
    )}/collection`;
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    };
    const tokens = [];
    let ref = 1;
    for (const type of types) {
      if (type === 'access_token' && !ignoreAccessToken) {
        tokens.push({
          ref: ref++,
          request_mode: 'urn:ext:oauth:request-mode:authorization',
          token_type_hint: 'access_token'
        });
      }
      if (type === 'secured_id_token') {
        tokens.push({
          ref: ref++,
          request_mode: 'urn:ext:oauth:request-mode:cookie',
          token_type_hint: 'urn:ext:oauth:token-type-hint:activity_ref_ssi'
        });
      }
      if (type === 'activity_ref') {
        const ermEnabled = getErmEnabledCookieValues();

        // if authRef is present in ermEnabled cookie, then pass that value as token in the body
        if (!isEmptyString(ermEnabled?.authRef)) {
          // request_mode does not need to be passed here bc body is handled as the default
          tokens.push({
            ref: ref++,
            token: ermEnabled.authRef,
            token_type_hint: 'urn:ext:oauth:token-type-hint:activity_ref'
          });
        }

        tokens.push({
          ref: ref++,
          request_mode: 'urn:ext:oauth:request-mode:cookie',
          token_type_hint: 'urn:ext:oauth:token-type-hint:activity_ref'
        });
      }
      if (type === 'remember_me') {
        tokens.push({
          ref: ref++,
          request_mode: 'urn:ext:oauth:request-mode:cookie',
          token_type_hint: 'urn:ext:oauth:token-type-hint:remember_me'
        });
      }
      if (type === 'id_token' && !ignoreAccessToken) {
        for (const idToken of idTokens) {
          tokens.push({
            ref: ref++,
            token: idToken,
            token_type_hint: 'urn:ext:oauth:token-type-hint:id_token'
          });
        }
      }
    }
    const requestBody = { tokens };
    const options = {
      body: requestBody,
      headers,
      method: 'POST',
      url
    };
    return fetchReq(options);
  }
};

export default API;
